import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { googleMapId, importGoogleLibrary } from '../../services/googleMaps';

const CUYO_CENTER = { lat: -32.95, lng: -67.9 };

const markerContent = (label, active = false) => {
  const element = document.createElement('div');
  element.className = `nido-google-marker${active ? ' is-active' : ''}`;
  element.textContent = label || '';
  return element;
};

const shortPrice = (property) => {
  const currency = property.currency === 'ARS' ? '$' : 'US$';
  const price = Number(property.price) || 0;
  if (price >= 1000000) return `${currency} ${(price / 1000000).toFixed(price % 1000000 ? 1 : 0)}M`;
  return `${currency} ${price >= 1000 ? `${Math.round(price / 1000)}k` : price}`;
};

export default function GoogleMap({ properties = [], highlightedPropertyId, editableLocation, onLocationChange, onPropertyClick, onPropertyHover }) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const onLocationChangeRef = useRef(onLocationChange);
  const onPropertyHoverRef = useRef(onPropertyHover);
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => { onLocationChangeRef.current = onLocationChange; }, [onLocationChange]);
  useEffect(() => { onPropertyHoverRef.current = onPropertyHover; }, [onPropertyHover]);

  useEffect(() => {
    let active = true;
    Promise.all([importGoogleLibrary('maps'), importGoogleLibrary('marker')])
      .then(([{ Map }, { AdvancedMarkerElement }]) => {
        if (!active || !hostRef.current) return;
        const map = new Map(hostRef.current, {
          center: CUYO_CENTER,
          zoom: 6,
          mapId: googleMapId,
          backgroundColor: '#ebe8df',
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: 'greedy',
        });
        mapRef.current = map;

        if (onLocationChangeRef.current) {
          map.addListener('click', (event) => onLocationChangeRef.current?.(event.latLng.lat(), event.latLng.lng()));
        }
        mapRef.current.AdvancedMarkerElement = AdvancedMarkerElement;
        setReady(true);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
      markersRef.current.forEach((marker) => { marker.map = null; });
      markersRef.current = [];
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const AdvancedMarkerElement = map?.AdvancedMarkerElement;
    if (!map || !AdvancedMarkerElement) return;
    markersRef.current.forEach((marker) => { marker.map = null; });

    const points = editableLocation
      ? [{ position: editableLocation, label: '', draggable: true }]
      : properties.flatMap((property) => {
        const lat = Number(property.location?.lat);
        const lng = Number(property.location?.lng);
        return Number.isFinite(lat) && Number.isFinite(lng)
          ? [{ property, position: { lat, lng }, label: shortPrice(property) }]
          : [];
      });

    markersRef.current = points.map(({ property, position, label, draggable }) => {
      const marker = new AdvancedMarkerElement({
        map,
        position,
        title: property?.title || 'Ubicación de la propiedad',
        gmpClickable: Boolean(property),
        gmpDraggable: draggable,
      });
      const content = markerContent(label, false);
      marker.append(content);
      marker.__nidoContent = content;
      marker.__nidoPropertyId = property?._id;
      if (draggable) marker.addListener('dragend', (event) => onLocationChangeRef.current?.(event.latLng.lat(), event.latLng.lng()));
      if (property) marker.addListener('click', () => onPropertyClick?.(property));
      if (property) {
        content.addEventListener('pointerenter', () => onPropertyHoverRef.current?.(property._id));
        content.addEventListener('pointerleave', () => onPropertyHoverRef.current?.(null));
      }
      return marker;
    });

    if (editableLocation) map.setOptions({ center: editableLocation, zoom: 15 });
    else if (points.length === 1) map.setOptions({ center: points[0].position, zoom: 14 });
    else if (points.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach(({ position }) => bounds.extend(position));
      map.fitBounds(bounds, 64);
    }
  }, [properties, editableLocation, onPropertyClick, ready]);

  useEffect(() => {
    markersRef.current.forEach((marker) => {
      marker.__nidoContent?.classList.toggle('is-active', String(marker.__nidoPropertyId) === String(highlightedPropertyId));
    });
  }, [highlightedPropertyId]);

  if (error) return <MapError>No pudimos cargar Google Maps. Revisá la configuración de la clave.</MapError>;
  return <MapHost ref={hostRef} aria-label="Mapa de Google" />;
}

const MapHost = styled.div`
  width:100%;height:100%;min-height:300px;background:#ebe8df;color-scheme:light;
  .gm-style{background:#ebe8df!important;}
  .nido-google-marker{min-width:82px;padding:10px 13px;color:#fff;background:#29474f;border:2px solid #fff;border-radius:10px;box-shadow:0 9px 24px rgba(32,55,60,.25);font-size:12px;font-weight:750;text-align:center;transform:translateY(-8px);}
  .nido-google-marker:empty{min-width:24px;width:24px;height:24px;padding:0;background:#c66f4e;border-radius:50%;box-shadow:0 0 0 7px rgba(198,111,78,.2),0 8px 24px rgba(32,55,60,.3);}
  .nido-google-marker.is-active{background:#c66f4e;transform:translateY(-12px) scale(1.08);}
`;
const MapError = styled.div`display:grid;width:100%;height:100%;min-height:300px;place-items:center;padding:24px;color:#657679;background:#ebe8df;text-align:center;`;
